import React from 'react';
import { ShieldCheck, Zap, Headphones, BadgePercent, Calendar, MessageCircle, Phone, Mail, MapPin, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { HERO_FEATURES, AGENT } from '../mock/mock';
import PosDevice from './PosDevice';

const iconMap = { ShieldCheck, Zap, Headphones, BadgePercent };

const Hero = () => {
  return (
    <section id="home" className="relative pt-[110px] pb-16 overflow-hidden">
      {/* Background layer */}
      <div className="absolute inset-0 -z-10 mesh-bg" />
      <div className="absolute -z-10 top-0 right-0 w-[600px] h-[600px] rounded-full bg-purple-200/40 blur-3xl" />
      <div className="absolute -z-10 top-40 left-1/3 w-[400px] h-[400px] rounded-full bg-violet-200/40 blur-3xl" />

      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-6 items-stretch">
          {/* LEFT */}
          <div className="lg:col-span-5 pt-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-purple-200 soft-shadow">
              <span className="w-2 h-2 rounded-full gradient-primary animate-pulse" />
              <span className="text-[11px] font-bold tracking-wider text-[#5F259F] uppercase">PhonePe Business Solutions</span>
            </div>

            <h1 className="mt-5 text-[44px] xl:text-[56px] leading-[1.05] font-extrabold text-slate-900 tracking-tight">
              Power Your Business
              <br />
              <span className="gradient-text">with Smart Payments</span>
            </h1>

            <p className="mt-5 text-[16px] leading-relaxed text-slate-600 max-w-[520px]">
              Get PhonePe Smart POS, QR, Soundbox and Payment Gateway with dedicated local support across Himachal Pradesh & Uttarakhand.
            </p>

            {/* Feature icons */}
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-[520px]">
              {HERO_FEATURES.map((f) => {
                const Icon = iconMap[f.icon];
                return (
                  <div key={f.title} className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl gradient-soft border border-purple-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#5F259F]" />
                    </div>
                    <div className="leading-tight">
                      <div className="text-[12px] font-bold text-slate-900">{f.title}</div>
                      <div className="text-[11px] text-slate-500">{f.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="mt-8 flex flex-wrap gap-3">
              <Button className="gradient-primary text-white rounded-2xl h-14 px-6 font-semibold btn-glow border-0 text-[15px]">
                <Calendar className="w-5 h-5 mr-2" />
                <div className="flex flex-col items-start leading-tight">
                  <span>Request Free Demo</span>
                  <span className="text-[11px] font-normal opacity-90">Get Best Offer</span>
                </div>
              </Button>
              <Button variant="outline" className="rounded-2xl h-14 px-6 border-2 border-emerald-500 bg-white text-emerald-600 font-semibold hover:bg-emerald-50 text-[15px]">
                <MessageCircle className="w-5 h-5 mr-2" />
                <div className="flex flex-col items-start leading-tight">
                  <span>Chat on WhatsApp</span>
                  <span className="text-[11px] font-normal opacity-80">Quick Response</span>
                </div>
              </Button>
            </div>

            {/* Trusted merchants row */}
            <div className="mt-7 flex items-center gap-3">
              <div className="flex -space-x-2">
                {['#5F259F', '#7C3AED', '#8B5CF6', '#A78BFA'].map((c, i) => (
                  <div key={i} className="w-9 h-9 rounded-full border-2 border-white flex items-center justify-center text-white text-[11px] font-bold" style={{ background: c }}>
                    {['R','N','A','P'][i]}
                  </div>
                ))}
              </div>
              <div className="text-sm text-slate-600 font-medium">
                Trusted by <span className="font-bold text-slate-900">1000+ merchants</span> across HP & UK
              </div>
            </div>
          </div>

          {/* CENTER - POS device */}
          <div className="lg:col-span-4 flex relative w-full h-full">
            <PosDevice />
          </div>

          {/* RIGHT - Agent card */}
          <div className="lg:col-span-3">
            <div className="glass-card rounded-3xl p-5 hover-lift">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-100 text-[#5F259F] text-[10px] font-bold uppercase tracking-wider">
                <CheckCircle2 className="w-3 h-3" />
                {AGENT.tag}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center text-white text-2xl font-black">
                    AS
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-white" />
                </div>
                <div>
                  <div className="font-extrabold text-slate-900 text-lg leading-tight">{AGENT.name}</div>
                  <div className="text-[12px] text-slate-500 font-medium">{AGENT.title}</div>
                </div>
              </div>

              <div className="mt-4 space-y-2.5">
                {[
                  { icon: Phone, label: AGENT.phone, color: 'text-[#5F259F]' },
                  { icon: MessageCircle, label: AGENT.whatsapp, color: 'text-emerald-600' },
                  { icon: Mail, label: AGENT.email, color: 'text-[#5F259F]', truncate: true },
                  { icon: MapPin, label: AGENT.coverage, color: 'text-[#5F259F]' },
                  { icon: Clock, label: AGENT.hours, color: 'text-[#5F259F]' },
                  { icon: CheckCircle2, label: AGENT.availability, color: 'text-emerald-600' },
                ].map((r, i) => {
                  const Icon = r.icon;
                  return (
                    <div key={i} className="flex items-center gap-2.5 text-[13px] text-slate-700">
                      <Icon className={`w-4 h-4 ${r.color} flex-shrink-0`} />
                      <span className={r.truncate ? 'truncate' : ''}>{r.label}</span>
                    </div>
                  );
                })}
              </div>

              <Button className="mt-4 w-full gradient-primary text-white rounded-xl h-11 font-semibold btn-glow border-0">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
